/**
 * Facturation de la suite, vue d'un front produit.
 *
 * L'argent vit au portail AuthSEmply (formules, abonnements, Stripe, webhook) ;
 * les écrans vivent dans chaque produit. Ce module est le client commun de ces
 * routes, pour que les quatre produits posent les mêmes questions au portail et
 * en tirent les mêmes conclusions — jusqu'ici chacun avait sa copie, et elles
 * avaient dérivé (libellés, couleurs, message de résiliation, contrôle du rôle).
 *
 * Même mécanique que `support.js` : le jeton vient de l'appelant (`getToken`),
 * ce paquet ne connaît aucun gestionnaire OIDC.
 */

function base(apiUrl) {
  return String(apiUrl ?? '').replace(/\/+$/, '');
}

/** Erreur du portail : porte son statut HTTP et son message métier. */
export class PortailFacturationError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'PortailFacturationError';
    this.status = status;
  }
}

/**
 * `getToken` du PORTAIL lui-même (11/10/2026) : pas de jeton, le cookie de
 * session SSO (routes `/espace/billing/*`, `apiUrl = <API>/espace`). Les
 * produits, eux, passent toujours une fonction rendant leur jeton.
 */
export const SESSION_PORTAIL = 'session-portail';

/** Options de `fetch` pour un appel au portail : jeton porteur, ou cookie de session. */
export async function optionsAppel(getToken) {
  if (getToken === SESSION_PORTAIL) return { credentials: 'include', entetes: {} };
  const jeton = typeof getToken === 'function' ? await getToken() : null;
  return { credentials: undefined, entetes: jeton ? { Authorization: `Bearer ${jeton}` } : {} };
}

async function appeler(apiUrl, getToken, chemin, corps) {
  const { credentials, entetes } = await optionsAppel(getToken);
  const reponse = await fetch(`${base(apiUrl)}${chemin}`, {
    method: corps === undefined ? 'GET' : 'POST',
    ...(credentials ? { credentials } : {}),
    headers: {
      accept: 'application/json',
      ...(corps === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...entetes,
    },
    ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
  });
  if (!reponse.ok) {
    const donnees = await reponse.json().catch(() => null);
    // Le message du PORTAIL, pas une phrase à nous : « No such price », « ce
    // code a expiré » ou « réservé au propriétaire » disent quoi faire, là où
    // « réessayez » invite à refaire ce qui vient d'échouer.
    const message = Array.isArray(donnees?.message)
      ? donnees.message.join(' ')
      : donnees?.message || `Le portail a répondu ${reponse.status}.`;
    throw new PortailFacturationError(message, reponse.status);
  }
  return reponse.json();
}

export const chargerFormules = (apiUrl, getToken) => appeler(apiUrl, getToken, '/billing/plans');
export const chargerFacturation = (apiUrl, getToken) => appeler(apiUrl, getToken, '/billing/account');
export const ouvrirPaiement = (apiUrl, getToken, params) =>
  appeler(apiUrl, getToken, '/billing/checkout', params);
export const apercuChangement = (apiUrl, getToken, params) =>
  appeler(apiUrl, getToken, '/billing/change/preview', params);
export const changerFormule = (apiUrl, getToken, params) =>
  appeler(apiUrl, getToken, '/billing/change', params);
export const resilier = (apiUrl, getToken, product) =>
  appeler(apiUrl, getToken, '/billing/cancel', { product, immediate: false });
export const reprendre = (apiUrl, getToken, product) =>
  appeler(apiUrl, getToken, '/billing/resume', { product });
export const verifierCode = (apiUrl, getToken, code, planId) =>
  appeler(apiUrl, getToken, '/billing/promo/verify', { code, planId });
export const activerCadeau = (apiUrl, getToken, code) =>
  appeler(apiUrl, getToken, '/billing/promo/redeem', { code });
/** Packs (04/10/2026) : en vente pour le produit, crédits restants, achats de l'espace. */
export const chargerPacks = (apiUrl, getToken, product) =>
  appeler(apiUrl, getToken, `/billing/packs?product=${encodeURIComponent(product)}`);
export const payerPack = (apiUrl, getToken, packId, params) =>
  appeler(apiUrl, getToken, `/billing/packs/${encodeURIComponent(packId)}/checkout`, params);

// ─── Règles d'affichage, communes aux quatre produits ──────────────────────

/**
 * Libellé et couleur de chaque statut. Les couleurs sont des JETONS de texte
 * de la charte : écrites en dur (#22c55e…) elles restaient identiques sous
 * les sept palettes et illisibles en thème sombre. Aucun statut ne se lit à la
 * couleur seule — le libellé porte l'information.
 */
export const STATUTS_ABONNEMENT = {
  ACTIVE: { label: 'Actif', color: 'var(--color-success-text)' },
  TRIALING: { label: "Période d'essai", color: 'var(--color-info)' },
  PAST_DUE: { label: 'Paiement en retard', color: 'var(--color-warning-text)' },
  INCOMPLETE: { label: 'Paiement à finaliser', color: 'var(--color-text-muted)' },
  CANCELED: { label: 'Résilié', color: 'var(--color-danger-text)' },
};

/** Ce que Stripe continuera de prélever : l'abonnement est « en cours ». */
export function estVivant(abonnement) {
  return Boolean(abonnement) && ['ACTIVE', 'TRIALING', 'PAST_DUE', 'INCOMPLETE'].includes(abonnement.status);
}

/**
 * Un abonnement payant qu'on MODIFIE (au lieu d'en ouvrir un second).
 * Un accès offert n'existe pas chez Stripe : on le remplace par une souscription.
 * Un abonnement INCOMPLET (premier paiement jamais abouti) non plus : Stripe
 * refuse de le modifier, on repasse par le paiement.
 */
export function seModifie(abonnement) {
  return estVivant(abonnement) && abonnement.source !== 'GIFT' && abonnement.status !== 'INCOMPLETE';
}

/** Une quantité saisie est-elle vendable ? Entier de 1 à `max`. */
export function quantiteValide(saisie, max = 100) {
  const n = Number(saisie);
  return Number.isInteger(n) && n >= 1 && n <= max;
}

/** « 1 dossier », « 3 dossiers ». */
export function compter(n, unite) {
  const u = unite ?? { singulier: 'dossier', pluriel: 'dossiers' };
  return `${n} ${n > 1 ? u.pluriel : u.singulier}`;
}

/** 20,5 → « 20,50 € ». */
export function euros(montant) {
  return `${Number(montant ?? 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

/**
 * La phrase d'échéance : renouvellement, fin programmée, fin d'un accès offert.
 * Elle était « Renouvellement le … » partout, y compris sur un abonnement
 * résilié qui ne serait justement pas renouvelé.
 */
export function phraseEcheance(abonnement) {
  if (!abonnement?.currentPeriodEnd) return '';
  const date = new Date(abonnement.currentPeriodEnd).toLocaleDateString('fr-FR');
  if (abonnement.source === 'GIFT') return `Accès offert jusqu’au ${date}`;
  if (abonnement.status === 'CANCELED') return `Terminé le ${date}`;
  if (abonnement.cancelAtPeriodEnd) return `Se termine le ${date} (résiliation programmée)`;
  return `Renouvellement le ${date}`;
}

/** Seuls le propriétaire et les administrateurs d'un espace engagent son paiement. */
export function peutGerer(role) {
  return role === 'OWNER' || role === 'ADMIN';
}
